import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { common } from "../../../../i18n";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import { AppDataGrid } from "../../../../components/datagrid/AppDataGrid";
import { CustomToolbar } from "../../../../components/datagrid/CustomToolbar";
import { useExternalApiKeyStore } from "../../../../contexts/useExternalApiKeyStore";
import { ExternalApiKey } from "../../../../service/externalApiKey.service";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import alertService from "../../../../components/alert/alertService";
import { useAuth } from "../../../../contexts/UserContext";
import CopyableField from "../../../../components/input/CopyableField";
import { useForm } from "react-hook-form";
import { InputReactHookForm } from "../../../../components/input/InputReactHookForm";
import CustomModal from "../../../../components/modal/CustomModal";
import TableApprovalStatus from "../../../emission/_components/data-table/TableApprovalStatus";
import { tableStyle } from "../../../emission/_components/data-table/EmissionDataTableStyle";

type EditDescriptionForm = {
  description: string;
};

export const ApiKeysTable = () => {
  const { isAdmin } = useAuth();

  const { keys, setOpenModal, revokeKey, regenerateKey, updateDescription } =
    useExternalApiKeyStore();

  const { t, i18n } = useTranslation([common], { nsMode: "fallback" });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [selectedRow, setSelectedRow] = useState<ExternalApiKey | null>(null);

  const open = Boolean(anchorEl);

  const [openEdit, setOpenEdit] = useState<boolean>(false);

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: any,
  ) => {
    event.stopPropagation();
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleRegenerate = async () => {
    if (!selectedRow) return;

    const isRegenerate = await alertService.confirm(
      t("Regenerate-Key"),
      `${t("Regenerate-Key-Description")}\n\n${t(
        "Regenerate-Key-Confirm-Question",
      )}`,
      t("Confirm-Regeneration"),
      undefined,
      true,
      undefined,
      t("Cancel"),
      "var(--white)",
    );

    if (isRegenerate) {
      const newKey = await regenerateKey(selectedRow.refId);

      if (newKey) {
        alertService.confirm(
          t("API-Key"),
          <CopyableField value={newKey} />,
          undefined,
          undefined,
          false,
          undefined,
          undefined,
          undefined,
          false,
          true,
        );
      }
    }

    handleCloseMenu();
  };

  const handleRevoke = async () => {
    if (!selectedRow) return;

    const isRevoke = await alertService.confirm(
      t("Revoke-API-Key"),
      `${t("Revoke-API-Key-Description")}\n\n${t(
        "Revoke-API-Key-Confirm-Question",
      )}`,
      t("Confirm"),
      "var(--red)",
      true,
      undefined,
      t("Cancel"),
      "var(--white)",
    );

    if (isRevoke) {
      await revokeKey(selectedRow.refId);
    }

    handleCloseMenu();
  };

  const {
    control,
    setValue,
    formState: { isSubmitting, isLoading },
    handleSubmit,
  } = useForm<EditDescriptionForm>({
    defaultValues: { description: "" },
  });

  async function submitEditDescription(data: EditDescriptionForm) {
    await updateDescription(selectedRow?.refId!, data.description);

    setOpenEdit(false);

    handleCloseMenu();
  }

  const handleEditDescription = async () => {
    if (!selectedRow) return;
    setValue("description", selectedRow.description);
    setOpenEdit(true);
  };

  const columns: GridColDef<ExternalApiKey>[] = [
    {
      field: "serviceName",
      headerName: t("Service-Name"),
      width: 160,
      flex: 1,
      disableColumnMenu: true,
    },
    {
      field: "description",
      headerName: t("Description"),
      width: 160,
      flex: 1,
      disableColumnMenu: true,
      valueFormatter: (value?: string) => {
        return value ?? "-";
      },
    },
    {
      field: "createdAt",
      headerName: t("Created-Date"),
      width: 160,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value: string) => {
        try {
          const start: Dayjs = dayjs(value.substring(0, 10));
          return start.locale(i18n.language).format("DD MMM YYYY");
        } catch (error) {
          return "-";
        }
      },
    },
    {
      field: "createdBy",
      headerName: t("Created-By"),
      width: 160,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value: any) => {
        try {
          return value.displayName;
        } catch (error) {
          return "-";
        }
      },
    },
    {
      field: "status",
      headerName: t("Status"),
      disableColumnMenu: true,
      width: 140,
      headerAlign: "center",
      renderCell: (params) => {
        return <TableApprovalStatus status={params.value} by={""} />;
      },
    },
    {
      field: "updatedAt",
      headerName: t("Last-Update"),
      width: 160,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value: string) => {
        try {
          const start: Dayjs = dayjs(value.substring(0, 10));
          return start.locale(i18n.language).format("DD MMM YYYY");
        } catch (error) {
          return "-";
        }
      },
    },
    {
      field: "lastUpdatedBy",
      headerName: t("Updated-By"),
      width: 160,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value: any) => {
        try {
          return value.displayName;
        } catch (error) {
          return "-";
        }
      },
    },
    {
      field: "lastUsed",
      headerName: t("Last-Used"),
      width: 160,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value: string) => {
        try {
          const start: Dayjs = dayjs(value.substring(0, 10));
          return start.locale(i18n.language).format("DD MMM YYYY");
        } catch (error) {
          return "-";
        }
      },
    },

    {
      field: "actions",
      type: "actions",
      headerName: "",
      width: 60,
      getActions: (params) =>
        isAdmin()
          ? [
              <GridActionsCellItem
                key={`${params.id}-more`}
                icon={<img src="/img/more.svg" alt="icon-more" />}
                label="More"
                onClick={(e) => handleOpenMenu(e as any, params.row)}
              />,
            ]
          : [],
    },
  ];

  return (
    <Box sx={{ height: "100%", width: "100%", minHeight: 0 }}>
      <CustomModal
        maxWidth="480px"
        header={
          <Typography variant="h2" color={"var(--dark)"}>
            {t("Edit-Description")}
          </Typography>
        }
        body={
          <form onSubmit={handleSubmit(submitEditDescription)}>
            <InputReactHookForm
              name="description"
              control={control}
              label={t("Description")}
              placeholder={t("Description")}
              required
              rules={{
                required: t("This-field-is-required"),
              }}
              showLabelInput={false}
            />

            <Box textAlign={"right"}>
              <Button
                variant="contained"
                color="secondary"
                type="submit"
                disabled={isSubmitting || isLoading}
                sx={{
                  mt: "40px",
                  minWidth: "160px",
                }}
              >
                {t("Edit")}
              </Button>
            </Box>
          </form>
        }
        open={openEdit}
        onClose={() => setOpenEdit(false)}
      />
      <AppDataGrid
        rows={keys}
        columns={columns}
        rowHeight={60}
        slots={{ toolbar: CustomToolbar }}
        slotProps={{
          toolbar: {
            button: (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<img src="/img/add-circle.svg" alt="add-circle" />}
                onClick={() => setOpenModal()}
                disabled={!isAdmin()}
              >
                {t("Create-API-Key")}
              </Button>
            ),
          },
        }}
        localeText={{
          toolbarQuickFilterPlaceholder: `${t("Search-Files")}...`,
          noRowsLabel: `${t("No-Data")}`,
          footerRowSelected: (count) => t("Row-Selected", { param: count }),
        }}
        pagination
        pageSizeOptions={[25, 50, 100]}
        sx={{
          ...tableStyle(),
          height: "100%",
        }}
        getRowId={(row) => row.refId}
        autosizeOnMount
      />

      {/* Dropdown menu per row */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {/* แถวสถานะ + เส้นคั่น + Revoke แบบในรูป */}
        <Box>
          <MenuItem onClick={handleEditDescription}>
            <Typography variant="text1">{t("Edit-Description")}</Typography>
          </MenuItem>

          <MenuItem onClick={handleRegenerate}>
            <Typography variant="text1">{t("Regenerated")}</Typography>
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleRevoke}>
            <Typography variant="text1" color={"red"}>
              {t("Delete")}
            </Typography>
          </MenuItem>
        </Box>
      </Menu>
    </Box>
  );
};
